import { extraQueryParams } from '@utils/constants';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import {
  addFavouriteAlbum,
  addFavouriteContributor,
  addFavouriteTrack,
  getAlbumById,
  getAlbumsByCategory,
  getFavouriteAlbums,
  getFavouriteAlbumsByCategory,
  getFavouriteAlbumsPaginated,
  getFavouriteContributors,
  getFavouriteContributorsPaginated,
  getFavouriteTracks,
  getFavouriteTracksPaginated,
  getFeaturedItems,
  removeFavouriteAlbum as removeFavouriteAlbumAPI,
  removeFavouriteContributor as removeFavouriteContributorAPI,
  removeFavouriteTrack as removeFavouriteTrackAPI,
  searchContent,
} from '../services/api';
import { Album, FeatureItem } from '../types';
import { showToast } from '../utils/toast';
import { useMusicState } from './storeHooks/useMusicState';

// Global request tracking to prevent duplicate API calls
const activeRequests = {
  music: null as Promise<{ items: Album[]; pagination: any }> | null,
  serenity: null as Promise<{ items: Album[]; pagination: any }> | null,
  audiobooks: null as Promise<{ items: Album[]; pagination: any }> | null,
  featured: null as Promise<FeatureItem[]> | null,
};

export const useAlbumData = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const {
    setMusicForYouList,
    setSerenityPlaylist,
    setWisdomPlaylist,
    setFeaturedItems,
    musicforyouList,
    serenityPlaylist,
    wisdomPlaylist,
    featuredItems,
    updateAlbumLikedStatus,
    updateTrackLikedStatus,
  } = useMusicState();
  const [musicLoading, setMusicLoading] = useState(false);
  const [serenityLoading, setSerenityLoading] = useState(false);
  const [wisdomLoading, setWisdomLoading] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    setFavouriteAlbums,
    setFavouriteContributors,
    setFavouriteTracks,
    addFavouriteAlbumToStore,
    addFavouriteTrackToStore,
    removeFavouriteAlbum,
    removeFavouriteTrack,
  } = useMusicState();
  // General loading state (true if any category is loading)
  const loading = musicLoading || serenityLoading || wisdomLoading;

  /**
   * Load music albums and update the store
   */
  const loadMusicAlbums = useCallback(
    async (limit: number = 10, offset: number = 0) => {
      // If data already exists, return it
      if (musicforyouList.length > 0) {
        return musicforyouList;
      }

      // If there's already an active request, set loading and return it
      if (activeRequests.music) {
        setMusicLoading(true);
        try {
          const result = await activeRequests.music;
          return result.items;
        } finally {
          setMusicLoading(false);
        }
      }

      try {
        setMusicLoading(true);
        setError(null);

        // Create and store the request promise
        const requestPromise = getAlbumsByCategory('music', limit, offset);
        activeRequests.music = requestPromise;

        const result = await requestPromise;
        const albums = result.items;
        setMusicForYouList(albums);

        return albums;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load music albums';
        setError(errorMessage);
        console.error('Error loading music albums:', err);
        throw err;
      } finally {
        setMusicLoading(false);
        activeRequests.music = null; // Clear the active request
      }
    },
    [setMusicForYouList, musicforyouList],
  );

  /**
   * Load serenity albums and update the store
   */
  const loadSerenityAlbums = useCallback(
    async (limit: number = 10, offset: number = 0) => {
      // If data already exists, return it
      if (serenityPlaylist.length > 0) {
        return serenityPlaylist;
      }

      // If there's already an active request, set loading and return it
      if (activeRequests.serenity) {
        setSerenityLoading(true);
        try {
          const result = await activeRequests.serenity;
          return result.items;
        } finally {
          setSerenityLoading(false);
        }
      }

      try {
        setSerenityLoading(true);
        setError(null);

        // Create and store the request promise
        const requestPromise = getAlbumsByCategory('serenity', limit, offset);
        activeRequests.serenity = requestPromise;

        const result = await requestPromise;
        const albums = result.items;
        setSerenityPlaylist(albums);

        return albums;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load serenity albums';
        setError(errorMessage);
        console.error('Error loading serenity albums:', err);
        throw err;
      } finally {
        setSerenityLoading(false);
        activeRequests.serenity = null; // Clear the active request
      }
    },
    [setSerenityPlaylist, serenityPlaylist],
  );

  /**
   * Load audiobooks albums and update the store
   */
  const loadWisdomAlbums = useCallback(
    async (limit: number = 10, offset: number = 0) => {
      // If data already exists, return it
      if (wisdomPlaylist.length > 0) {
        return wisdomPlaylist;
      }

      // If there's already an active request, set loading and return it
      if (activeRequests.audiobooks) {
        setWisdomLoading(true);
        try {
          const result = await activeRequests.audiobooks;
          return result.items;
        } finally {
          setWisdomLoading(false);
        }
      }

      try {
        setWisdomLoading(true);
        setError(null);

        // Create and store the request promise
        const requestPromise = getAlbumsByCategory('book', limit, offset);
        activeRequests.audiobooks = requestPromise;

        const result = await requestPromise;
        const albums = result.items;
        setWisdomPlaylist(albums);

        return albums;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load audiobooks albums';
        setError(errorMessage);
        console.error('Error loading audiobooks albums:', err);
        throw err;
      } finally {
        setWisdomLoading(false);
        activeRequests.audiobooks = null; // Clear the active request
      }
    },
    [setWisdomPlaylist, wisdomPlaylist],
  );

  /**
   * Load albums by category
   */
  const loadAlbumsByCategory = useCallback(
    async (
      category: string,
      limit: number = 10,
      offset: number = 0,
    ): Promise<{ items: Album[]; pagination: any }> => {
      try {
        setError(null);
        const albums = await getAlbumsByCategory(category, limit, offset);
        return albums;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : `Failed to load ${category} albums`;
        setError(errorMessage);
        console.error(`Error loading ${category} albums:`, err);
        throw err;
      }
    },
    [],
  );

  /**
   * Load featured albums and update the store
   */
  const loadFeaturedItems = useCallback(async () => {
    // If data already exists, return it
    if (featuredItems.length > 0) {
      return featuredItems;
    }

    // If there's already an active request, set loading and return it
    if (activeRequests.featured) {
      setFeaturedLoading(true);
      try {
        const albums = await activeRequests.featured;
        return albums;
      } finally {
        setFeaturedLoading(false);
      }
    }

    try {
      setFeaturedLoading(true);
      setError(null);

      const albums = await getFeaturedItems();
      setFeaturedItems(albums.items);

      return albums;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load audiobooks albums';
      setError(errorMessage);
      console.error('Error loading audiobooks albums:', err);
      throw err;
    } finally {
      setFeaturedLoading(false);
      activeRequests.audiobooks = null; // Clear the active request
    }
  }, [setFeaturedItems, featuredItems]);

  const loadAlbumById = useCallback(async (albumId: string) => {
    try {
      const album = await getAlbumById(albumId);
      return album;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load album';
      setError(errorMessage);
      console.error('Error loading album:', err);
      throw err;
    }
  }, []);

  /**
   * Load albums by category with pagination support for infinite scroll
   */
  const loadAlbumsByCategoryPaginated = useCallback(
    async (
      category: string,
      limit: number = 10,
      offset: number = 0,
      genre?: string,
      search?: string,
    ): Promise<{ items: Album[]; hasNext: boolean }> => {
      try {
        const result = await getAlbumsByCategory(
          category,
          limit,
          offset,
          genre,
          search,
        );
        return {
          items: result?.items || [],
          hasNext: result?.pagination?.hasNext || false,
        };
      } catch (err) {
        console.error(`Error loading ${category} albums:`, err);
        throw err;
      }
    },
    [],
  );

  /**
   * Search albums via the global search API (`/api/search`) — the same
   * endpoint used by the header search. Maps the search results into the
   * album shape expected by the music grid.
   */
  const searchAlbumsPaginated = useCallback(
    async (
      limit: number = 12,
      offset: number = 0,
      search?: string,
    ): Promise<{ items: Album[]; hasNext: boolean }> => {
      try {
        const response = await searchContent(
          (search || '').trim(),
          'Album',
          limit,
          offset,
        );
        const items = response.items.map(
          (result) =>
            ({
              id: result.id,
              upc: '',
              albumName: result.name,
              albumArtUrl: result.albumArtUrl || result.thumbnailUrl || '',
              thumbnailUrl: result.thumbnailUrl,
              description: result.description || '',
              category: result.category || 'music',
              genre: [],
              tracks: [],
              contributors: result.contributors || [],
              likes: 0,
              type: 'search',
            }) as unknown as Album,
        );
        return {
          items,
          hasNext: response.pagination.hasNext,
        };
      } catch (err) {
        console.error('Error searching albums:', err);
        throw err;
      }
    },
    [],
  );

  /**
   * Load music albums with pagination support for infinite scroll.
   * When a search query is present, use the global search API (same as the
   * header search); otherwise fall back to the category/genre album listing.
   */
  const loadMusicAlbumsPaginated = useCallback(
    async (
      limit: number = 10,
      offset: number = 0,
      genre?: string,
      search?: string,
    ): Promise<{ items: Album[]; hasNext: boolean }> => {
      if (search && search.trim()) {
        return searchAlbumsPaginated(limit, offset, search);
      }
      return loadAlbumsByCategoryPaginated(
        'music',
        limit,
        offset,
        genre,
        search,
      );
    },
    [loadAlbumsByCategoryPaginated, searchAlbumsPaginated],
  );

  /**
   * Load serenity albums with pagination support for infinite scroll
   */
  const loadSerenityAlbumsPaginated = useCallback(
    async (
      limit: number = 10,
      offset: number = 0,
      genre?: string,
      search?: string,
    ): Promise<{ items: Album[]; hasNext: boolean }> => {
      return loadAlbumsByCategoryPaginated(
        'serenity',
        limit,
        offset,
        genre,
        search,
      );
    },
    [loadAlbumsByCategoryPaginated],
  );

  /**
   * Load audiobooks albums with pagination support for infinite scroll
   */
  const loadWisdomAlbumsPaginated = useCallback(
    async (
      limit: number = 10,
      offset: number = 0,
      genre?: string,
      search?: string,
    ): Promise<{ items: Album[]; hasNext: boolean }> => {
      return loadAlbumsByCategoryPaginated(
        'book',
        limit,
        offset,
        genre,
        search,
      );
    },
    [loadAlbumsByCategoryPaginated],
  );

  /**
   * Load Indian music albums with pagination support for infinite scroll
   * Uses category=music with genre=indian music (or specific genre when filtered)
   */
  const loadIndianMusicAlbumsPaginated = useCallback(
    async (
      limit: number = 10,
      offset: number = 0,
      genre?: string,
      search?: string,
    ): Promise<{ items: Album[]; hasNext: boolean }> => {
      // If a specific genre is selected, use that genre; otherwise use "indian music"
      const indianMusicGenre =
        genre && genre.toLowerCase() !== 'all' ? genre : 'indian music';
      return loadAlbumsByCategoryPaginated(
        'music',
        limit,
        offset,
        indianMusicGenre,
        search,
      );
    },
    [loadAlbumsByCategoryPaginated],
  );

  const addFavouriteItem = useCallback(
    async (type: string, id: string, isLiked: boolean = false) => {
      // Check if user is authenticated
      if (!auth.isAuthenticated) {
        console.log('User not authenticated, redirecting to login...');
        const fullPath = window.location.pathname + window.location.search;
        auth
          .signinRedirect({
            extraQueryParams,
            state: { returnUrl: fullPath },
          })
          .catch((error) => {
            console.error('Error during sign-in redirect:', error);
          });
        return;
      }

      // Find album name for better toast messages
      let albumName = '';
      if (type === 'album') {
        const album = [
          ...musicforyouList,
          ...serenityPlaylist,
          ...wisdomPlaylist,
        ].find((album) => album.id === id);
        albumName = album?.albumName || '';
      }

      try {
        // Update the state immediately for optimistic UI update
        if (type === 'album') {
          updateAlbumLikedStatus(id, !isLiked);
        } else if (type === 'track') {
          updateTrackLikedStatus(id, !isLiked);
        } else if (type === 'contributor') {
          // Update featured items for contributors as well
          updateAlbumLikedStatus(id, !isLiked);
        }
        if (isLiked === true) {
          if (type === 'album') {
            showToast.unliked(albumName);
            removeFavouriteAlbumAPI(id);
            // Remove from Redux store immediately
            removeFavouriteAlbum(id);
          } else if (type === 'contributor') {
            showToast.unliked();
            removeFavouriteContributorAPI(id);
          } else if (type === 'track') {
            showToast.unliked();
            removeFavouriteTrackAPI(id);
            // Remove from Redux store immediately
            removeFavouriteTrack(id);
          }
        } else {
          if (type === 'album') {
            showToast.liked(albumName);
            addFavouriteAlbum(id);
            // Add to Redux store immediately
            const album = [
              ...musicforyouList,
              ...serenityPlaylist,
              ...wisdomPlaylist,
            ].find((album) => album.id === id);
            if (album) {
              const favoriteAlbumData = {
                id: album.id,
                name: album.albumName,
                albumArtUrl: album.albumArtUrl,
                contributors: album.contributors,
                category: album.category || 'music', // Default to music if no category
                createdAt: new Date().toISOString(),
              };
              addFavouriteAlbumToStore(favoriteAlbumData);
            }
          } else if (type === 'contributor') {
            showToast.liked();
            addFavouriteContributor(id);
          } else if (type === 'track') {
            showToast.liked();
            addFavouriteTrack(id);
            const basicTrackData = {
              id: id,
              name: 'Track', // This should be the actual track name
              createdAt: new Date().toISOString(),
            };
            addFavouriteTrackToStore(basicTrackData);
          }
        }
      } catch (err) {
        console.error('Error adding favourite item:', err);
        // Revert the optimistic update on error
        if (type === 'album') {
          updateAlbumLikedStatus(id, isLiked);
        } else if (type === 'track') {
          updateTrackLikedStatus(id, isLiked);
        } else if (type === 'contributor') {
          // Revert the optimistic update for contributors
          updateAlbumLikedStatus(id, isLiked);
        }
        // Show error toast
        showToast.error(t('common.failedToUpdateFavorites'));
        throw err;
      }
    },
    [
      auth,
      updateAlbumLikedStatus,
      updateTrackLikedStatus,
      musicforyouList,
      serenityPlaylist,
      wisdomPlaylist,
      addFavouriteAlbumToStore,
      addFavouriteTrackToStore,
      removeFavouriteAlbum,
      removeFavouriteTrack,
    ],
  );

  const getFavouriteItems = useCallback(async (type: string) => {
    try {
      if (type === 'album') {
        const result = await getFavouriteAlbums();
        setFavouriteAlbums(result.items);
      } else if (type === 'contributor') {
        const result = await getFavouriteContributors();
        setFavouriteContributors(result.items);
      } else if (type === 'track') {
        const result = await getFavouriteTracks();
        setFavouriteTracks(result.items);
      }
    } catch (err) {
      console.error('Error loading featured items:', err);
      throw err;
    }
  }, []);

  /**
   * Load favorite albums with pagination support for infinite scroll
   */
  const loadFavouriteAlbumsPaginated = useCallback(
    async (
      limit: number = 12,
      offset: number = 0,
    ): Promise<{ items: any[]; hasNext: boolean }> => {
      try {
        const result = await getFavouriteAlbumsPaginated(limit, offset);
        return {
          items: result?.items || [],
          hasNext: result?.hasNext || false,
        };
      } catch (err) {
        console.error('Error loading favorite albums:', err);
        throw err;
      }
    },
    [],
  );

  /**
   * Load favorite tracks with pagination support for infinite scroll
   */
  const loadFavouriteTracksPaginated = useCallback(
    async (
      limit: number = 12,
      offset: number = 0,
    ): Promise<{ items: any[]; pagination: any }> => {
      try {
        const result = await getFavouriteTracksPaginated(limit, offset);
        return {
          items: result?.items || [],
          pagination: result?.pagination || { hasNext: false, total: 0 },
        };
      } catch (err) {
        console.error('Error loading favorite tracks:', err);
        throw err;
      }
    },
    [],
  );

  /**
   * Load favorite albums by category with pagination support
   */
  const loadFavouriteAlbumsByCategory = useCallback(
    async (
      category: 'music' | 'serenity' | 'book',
      limit: number = 12,
      offset: number = 0,
    ): Promise<{ items: any[]; pagination: any }> => {
      try {
        const result = await getFavouriteAlbumsByCategory(
          category,
          limit,
          offset,
        );
        return {
          items: result?.items || [],
          pagination: result?.pagination || { hasNext: false, total: 0 },
        };
      } catch (err) {
        console.error(`Error loading favorite ${category} albums:`, err);
        throw err;
      }
    },
    [],
  );

  /**
   * Load favorite contributors with pagination support
   */
  const loadFavouriteContributorsPaginated = useCallback(
    async (
      limit: number = 12,
      offset: number = 0,
    ): Promise<{ items: any[]; pagination: any }> => {
      try {
        const result = await getFavouriteContributorsPaginated(limit, offset);
        return {
          items: result?.items || [],
          pagination: result?.pagination || { hasNext: false, total: 0 },
        };
      } catch (err) {
        console.error('Error loading favorite contributors:', err);
        throw err;
      }
    },
    [],
  );

  return {
    loading,
    musicLoading,
    serenityLoading,
    wisdomLoading,
    featuredLoading,
    error,
    loadMusicAlbums,
    loadSerenityAlbums,
    loadWisdomAlbums,
    loadAlbumsByCategory,
    loadMusicAlbumsPaginated,
    searchAlbumsPaginated,
    loadSerenityAlbumsPaginated,
    loadWisdomAlbumsPaginated,
    loadIndianMusicAlbumsPaginated,
    loadAlbumsByCategoryPaginated,
    loadAlbumById,
    loadFeaturedItems,
    addFavouriteItem,
    getFavouriteItems,
    loadFavouriteAlbumsPaginated,
    loadFavouriteTracksPaginated,
    loadFavouriteAlbumsByCategory,
    loadFavouriteContributorsPaginated,
  };
};

export default useAlbumData;
